/**
 * [PM-SG 2026-08-11] The navigation dock — a hairline on the bottom edge that opens into a
 * glass pill when the cursor reaches it.
 *
 * A permanent tab row would spend screen on navigation that nobody standing in front of a wall
 * display can use anyway. A 2px line spends almost nothing and is still discoverable the
 * moment somebody picks up a mouse.
 *
 * Opens instantly, closes on a delay — "unhover intent". Delaying both feels sluggish; delaying
 * neither snaps the menu shut while the cursor is still travelling towards it. While open, an
 * invisible bridge spans line-to-pill so the pointer can cross the gap; it only exists while
 * open, so hovering the board underneath can never trigger the menu.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { RED, TEXT, EASE, FONT } from './chrome';
import { isMuted, setMuted } from './sound';

/* [PM-SG 2026-08-11] Five pages, capped deliberately. GATE CLEARED was retired: it is a
   celebration carrying no figure that NOTIFIER does not already carry, and on a five-page
   rotation a screen that often reads "STANDING BY" is the one you can least afford. */
export type PageId = 'race' | 'pitwall' | 'timing' | 'podium' | 'debrief' | 'notifier';

export const PAGES: Array<{ id: PageId; label: string }> = [
  { id: 'race', label: 'RACE' },
  { id: 'pitwall', label: 'PIT WALL' },
  { id: 'timing', label: 'TIMING' },
  { id: 'podium', label: 'PARC FERMÉ' },
  // [PM-SG DEBRIEF 2026-09-18, user] Sits directly after PARC FERMÉ — the top three, then what
  // everybody behind them has to do to get there. Replaced ROLL OF HONOUR.
  { id: 'debrief', label: 'PIT DEBRIEF' },
  { id: 'notifier', label: 'NOTIFIER' },
];

const Dock = styled.div<{ $open: boolean }>`
  /* [PM-SG CANVAS] Absolute, not fixed: the dock sits inside the scaled canvas so it stays the
     same size relative to the board on every screen, and stays off the letterbox bars. */
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  z-index: 70;
  pointer-events: none;

  ${(p) => p.$open && `
    &::before {
      content: ''; position: absolute; left: -40px; right: -40px; bottom: 0; height: 130px;
      pointer-events: auto;
    }
  `}
`;

/* The bar sits flush on the screen edge; the button is only tall enough to give it a hover
   target reaching upward, since 2px cannot be hovered. */
const Line = styled.button<{ $open: boolean }>`
  pointer-events: auto;
  position: relative;
  display: block;
  margin: 0 auto;
  width: 220px;
  height: 22px;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;

  &::before {
    content: '';
    position: absolute; left: 50%; bottom: 0; transform: translateX(-50%);
    width: ${(p) => (p.$open ? '250px' : '170px')};
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: ${(p) => (p.$open ? 'rgba(225,6,0,.82)' : 'rgba(225,6,0,.42)')};
    box-shadow: ${(p) => (p.$open ? '0 0 22px rgba(225,6,0,.42)' : '0 0 14px rgba(225,6,0,.18)')};
    transition: width 320ms ${EASE}, background 320ms ease, box-shadow 320ms ease;
  }
  &:hover::before, &:focus-visible::before {
    width: 250px;
    background: rgba(225, 6, 0, 0.82);
    box-shadow: 0 0 22px rgba(225, 6, 0, 0.42);
  }
  &:focus-visible { outline: 2px solid rgba(255,255,255,.5); outline-offset: 4px; border-radius: 4px; }
`;

/* Liquid glass. visibility:hidden while closed so the backdrop-filter is not recompositing
   every frame behind the fog and the lap animation. */
const Pill = styled.div<{ $open: boolean }>`
  position: absolute;
  bottom: 34px;
  left: 50%;
  transform-origin: 50% 100%;
  transform: translateX(-50%) ${(p) => (p.$open ? 'scaleX(1) translateY(0)' : 'scaleX(.25) translateY(10px)')};
  display: flex;
  gap: 6px;
  padding: 8px;
  background: rgba(18, 18, 26, 0.55);
  -webkit-backdrop-filter: blur(24px) saturate(1.5);
  backdrop-filter: blur(24px) saturate(1.5);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  box-shadow: 0 20px 54px rgba(0, 0, 0, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  opacity: ${(p) => (p.$open ? 1 : 0)};
  visibility: ${(p) => (p.$open ? 'visible' : 'hidden')};
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
  transition: ${(p) => (p.$open
    ? `opacity 320ms ${EASE}, transform 320ms ${EASE}, visibility 0s`
    : 'opacity 180ms ease, transform 180ms ease, visibility 0s 180ms')};
`;

const Item = styled.button<{ $current: boolean; $open: boolean }>`
  position: relative;
  font-family: ${FONT};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: ${TEXT};
  border: 0;
  border-radius: 999px;
  padding: 10px 22px;
  cursor: pointer;
  white-space: nowrap;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  transform: ${(p) => (p.$open ? 'none' : 'translateY(8px)')};
  transition: opacity 260ms ${EASE}, transform 260ms ${EASE}, background 160ms ease;
  transition-delay: ${(p) => (p.$open ? 'calc(var(--i) * 40ms + 100ms)' : '0s')};

  /* Current page reads as a STATE, not a button. A solid red fill with a glow looks like
     something you press; a translucent tint behind a hairline rim looks like where you already
     are. Inset shadow rather than a border, so the item does not change size between states. */
  background: ${(p) => (p.$current ? 'rgba(225,6,0,.15)' : 'transparent')};
  box-shadow: ${(p) => (p.$current ? 'inset 0 0 0 1px rgba(225,6,0,.55)' : 'none')};

  &:hover { background: ${(p) => (p.$current ? 'rgba(225,6,0,.24)' : 'rgba(255,255,255,.10)')}; }
  &:focus-visible { outline: 2px solid rgba(255,255,255,.6); outline-offset: 2px; }

  &::before {
    content: '';
    position: absolute; left: 9px; top: 50%; margin-top: -7px;
    width: 3px; height: 14px; background: ${RED};
    transform: skewX(-18deg) scaleY(0);
    transition: transform 180ms ${EASE};
    display: ${(p) => (p.$current ? 'none' : 'block')};
  }
  &:hover::before { transform: skewX(-18deg) scaleY(1); }
`;

interface Props {
  current: PageId;
  onSelect: (id: PageId) => void;
}

const NavDock: React.FC<Props> = ({ current, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [muted, setMutedState] = useState(isMuted);
  const closeTimer = useRef(0);

  const doOpen = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  }, []);
  const scheduleClose = useCallback(() => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 280);
  }, []);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  return (
    <Dock $open={open} onMouseLeave={scheduleClose} onMouseEnter={doOpen}>
      <Pill $open={open} role="tablist" aria-hidden={!open}>
        {PAGES.map((p, i) => (
          <Item
            key={p.id}
            $current={p.id === current}
            $open={open}
            style={{ '--i': i } as React.CSSProperties}
            aria-current={p.id === current ? 'page' : 'false'}
            onClick={() => { onSelect(p.id); setOpen(false); }}
          >
            {p.label}
          </Item>
        ))}
        {/* The announcement sound, on by default. It lives here rather than on the announcement
            page because somebody wanting silence needs to reach it from wherever the board
            happens to be, and the choice is remembered across the deploy-reload. */}
        <Item
          $current={false}
          $open={open}
          style={{ '--i': PAGES.length } as React.CSSProperties}
          aria-pressed={!muted}
          title={muted ? 'Announcement sound off' : 'Announcement sound on'}
          onClick={() => { const next = !muted; setMuted(next); setMutedState(next); }}
        >
          {muted ? '🔇' : '🔊'}
        </Item>
      </Pill>
      <Line
        $open={open}
        type="button"
        aria-expanded={open}
        aria-label="Board pages"
        onMouseEnter={doOpen}
        onFocus={doOpen}
        onClick={() => setOpen((o) => !o)}
      />
    </Dock>
  );
};

/**
 * [PM-SG SMOOTH 2026-08-13] Memoised. The board shell re-renders on its own state — the page
 * rotation tick, the sweep, an announcement, the diagnostic toggle, a resize — none of which
 * change this view's props. Without this, each of those re-rendered the whole view mid-animation.
 */
export default React.memo(NavDock);
